<template>
  <div class="results-slideshow">
    <sp-slider
      ref="list"
      v-preserve-properties:slides-per-view="1"
      v-preserve-properties:breakpoints="SLIDESHOW_BREAKPOINTS"
      class="results-slideshow__slider"
      infinite
      arrows
      dots
      :region-label="resultText('carousel_label')"
    >
      <div v-for="{ result, tag, properties } in cards" :key="result.resultGUID" class="results-slideshow__slide">
        <div class="results-slideshow__frame">
          <FinderResultCard class="results-slideshow__card" :tag="tag" :result="result" :properties="properties" />
        </div>
      </div>
    </sp-slider>
  </div>
</template>

<script setup>
import { useTemplateRef } from "vue";
import { vPreserveProperties } from "#ce/utils/property-bindings";
import { resultText } from "#ce/components/SpFinder/view-models/result-text";
import { useResultElements } from "#ce/components/SpFinder/composables/useResultElements";
import { useResultEvents } from "#ce/components/SpFinder/composables/useResultEvents";
import FinderResultCard from "#ce/components/SpFinder/components/FinderResultCard.ce.vue";

// Keyed by the slider's width, which is the list's plus the arrows' 6px overhang on each side: one card, two
// from a 835px list and three from a 1159px list.
const SLIDESHOW_BREAKPOINTS = Object.freeze({
  0: { slidesPerView: 1 },
  847: { slidesPerView: 2 },
  1171: { slidesPerView: 3 },
});

const props = defineProps({
  records: {
    type: Array,
    required: true,
  },
  kind: {
    type: Object,
    default: undefined,
  },
  payload: {
    type: Object,
    required: true,
  },
  settings: {
    type: Object,
    default: undefined,
  },
  commands: {
    type: Object,
    required: true,
  },
});

const list = useTemplateRef("list");
const cards = useResultElements(
  () => props.records,
  () => props.kind,
  () => ({ payload: props.payload, settings: props.settings }),
);

useResultEvents(list, () => props.commands);
</script>

<style lang="scss" scoped>
@use "@assets/mixins/breakpoint" as *;
@use "@ce/components/SpFinder/styles/results-container" as *;

.results-slideshow {
  --card-padding: var(--sp-comp-finder-results-slideshow-card-padding, var(--sp-ref-scale-20));
  --arrow-band: var(--sp-comp-finder-results-slideshow-arrow-band, 67px);
  --slides-padding-block: var(--sp-comp-finder-results-slideshow-padding-block, var(--sp-ref-scale-32));

  @include finder-results-container;

  container: results-slideshow / inline-size;
  margin-block-end: var(--sp-comp-finder-results-slideshow-margin-block-end, var(--sp-ref-scale-32));
  padding-block-end: var(--sp-comp-finder-results-slideshow-padding-block-end, var(--sp-ref-scale-64));
  padding-inline: 0;

  @include sp-breakpoint(xs) {
    --arrow-band: var(--sp-comp-finder-results-slideshow-arrow-band-xs, 30px);
  }

  &__slider {
    --sp-comp-slider-gap: 0px;
    --sp-comp-slider-controls-gap: 0px;
    --sp-comp-slider-arrow-track-size: calc(var(--arrow-band) + 6px);
    --sp-comp-slider-arrow-offset: 0px;
    --sp-comp-slider-dots-margin-top: 0px;
    --sp-comp-slider-dots-min-height: var(--sp-comp-finder-results-slideshow-dots-band, var(--sp-ref-scale-18));
    --sp-comp-slider-dot-gap: var(--sp-ref-scale-10);
    --sp-comp-slider-dot-cell-display: grid;
    --sp-comp-slider-dot-target-size: var(--sp-ref-scale-18);
    --sp-comp-slider-dot-color: transparent;
    --sp-comp-slider-dot-active-color: transparent;
    --sp-comp-slider-dot-mark-display: block;
    --sp-comp-slider-dot-mark-size: var(--sp-ref-scale-12);
    --sp-comp-slider-dot-mark-color: color-mix(in srgb, var(--sp-ref-palette-black) 25%, transparent);
    --sp-comp-slider-dot-mark-active-color: color-mix(in srgb, var(--sp-ref-palette-black) 75%, transparent);

    display: flex;
    width: calc(100% + 12px);
    margin-inline: -6px;
  }

  // Between one card and two, the arrows hide and keep their bands.
  @container results-slideshow (561px <= width < 835px) {
    &__slider {
      --sp-comp-slider-arrow-visibility: hidden;
    }
  }

  &__slide {
    box-sizing: border-box;
    align-self: stretch;
    width: 100%;
    height: 100%;
    --half-gap: calc(var(--sp-comp-finder-results-slideshow-gap, var(--sp-ref-scale-32)) / 2);

    padding: calc(var(--slides-padding-block) + var(--half-gap)) var(--half-gap);
  }

  &__frame {
    box-sizing: border-box;
    height: 100%;
    background-color: var(--sp-comp-finder-results-slideshow-card-background, var(--sp-ref-palette-white));
    border: var(
      --sp-comp-finder-results-slideshow-card-border,
      1px solid color-mix(in srgb, var(--sp-ref-palette-black) 15.3%, var(--sp-ref-palette-white))
    );
    border-radius: var(--sp-comp-finder-results-slideshow-card-border-radius, var(--sp-ref-scale-6));
    box-shadow: var(--sp-comp-finder-results-slideshow-card-shadow, var(--sp-ref-shadow-4));
    text-align: start;
  }

  &__card {
    --sp-comp-finder-result-padding-block: var(--card-padding);
    --sp-comp-finder-result-padding-inline: 0;
    --sp-comp-finder-result-heading-padding: var(--card-padding) var(--card-padding) 0;
    --sp-comp-finder-result-details-padding: 0 var(--card-padding);
    --sp-comp-finder-result-scheduling-padding: var(--card-padding);
    --sp-comp-finder-result-scheduling-border-top: 1px solid
      color-mix(in srgb, var(--sp-ref-palette-black) 10.2%, var(--sp-ref-palette-white));
    --sp-comp-finder-result-thumbnail-image-size: var(--sp-ref-scale-128);
    --sp-comp-finder-result-grid-template-rows: auto auto 1fr auto;
    --sp-comp-finder-result-grid-template-areas-xs: "thumbnail" "heading" "details" "scheduling";
    --sp-comp-finder-result-heading-text-align: center;

    display: block;
    height: 100%;
  }
}
</style>
